import { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { toast } from 'react-toastify'
import PageContainer from '../components/layout/PageContainer'
import StatCard from '../components/common/StatCard'
import AuditTrailSection from '../components/compliance/AuditTrailSection'
import RemediationLogSection from '../components/compliance/RemediationLogSection'
import ComplianceReportSection from '../components/compliance/ComplianceReportSection'
import { websiteService } from '../services/api/websiteService'
import { complianceService } from '../services/api/complianceService'
import { formatScoreChange } from '../utils/complianceReportHelpers'

const TABS = [
  {
    id: 'audit',
    label: 'Audit Trail',
    icon: 'mdi:timeline-clock-outline',
  },
  {
    id: 'remediation',
    label: 'Remediation Log',
    icon: 'mdi:clipboard-check-outline',
  },
  {
    id: 'report',
    label: 'Compliance Report',
    icon: 'mdi:file-pdf-box',
  },
]

const REMEDIATIONS_LIMIT = 10
const WEBSITES_LIMIT = 10

const getWebsiteLabel = (w) => w.siteName || w.name || w.siteUrl || w.domain || 'Unnamed Site'
const getWebsiteId = (w) => w?._id || w?.id

const WebsiteSelect = ({
  websites,
  selectedWebsiteId,
  onChange,
  onLoadMore,
  hasMore,
  isLoadingMore,
}) => {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const selected = websites.find((w) => getWebsiteId(w) === selectedWebsiteId)

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleScroll = (e) => {
    if (!onLoadMore || !hasMore || isLoadingMore) return
    const { scrollTop, scrollHeight, clientHeight } = e.target
    if (scrollHeight - scrollTop - clientHeight < 40) onLoadMore()
  }

  return (
    <div ref={ref} className="relative w-full sm:w-72">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`w-full flex items-center justify-between gap-2 px-3.5 py-2.5 rounded-lg border bg-white text-left shadow-sm transition-colors
          ${open ? 'border-primary ring-2 ring-primary/20' : 'border-gray-300 hover:border-primary'}
        `}
      >
        <span className="flex-1 min-w-0 flex items-center gap-2">
          <Icon icon="mdi:web" className="text-primary shrink-0" />
          <span className="min-w-0">
            {selected ? (
              <span className="block text-sm font-semibold text-gray-800 truncate">
                {getWebsiteLabel(selected)}
              </span>
            ) : (
              <span className="block text-sm text-gray-400">Select a website...</span>
            )}
            {selected?.siteUrl && (
              <span className="block text-[11px] text-gray-400 truncate">{selected.siteUrl}</span>
            )}
          </span>
        </span>
        <Icon
          icon={open ? 'mdi:chevron-up' : 'mdi:chevron-down'}
          className="text-gray-400 text-lg shrink-0"
        />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          <div className="max-h-64 overflow-y-auto" onScroll={handleScroll}>
            {websites.map((w) => {
              const id = getWebsiteId(w)
              const isSelected = id === selectedWebsiteId
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => {
                    onChange(id)
                    setOpen(false)
                  }}
                  className={`w-full text-left px-3.5 py-2.5 hover:bg-primary/5 transition-colors border-b border-gray-100 last:border-b-0
                    ${isSelected ? 'bg-primary/10' : ''}
                  `}
                >
                  <p className={`text-sm font-medium truncate ${isSelected ? 'text-primary' : 'text-gray-800'}`}>
                    {getWebsiteLabel(w)}
                  </p>
                  {w.siteUrl && (
                    <p className="text-[11px] text-gray-400 truncate mt-0.5">{w.siteUrl}</p>
                  )}
                </button>
              )
            })}
            {isLoadingMore && (
              <div className="flex items-center justify-center gap-2 py-2 text-xs text-gray-400">
                <Icon icon="mdi:loading" className="animate-spin" />
                Loading more...
              </div>
            )}
          </div>
          <div className="px-3.5 py-1.5 bg-gray-50 border-t border-gray-200 text-[11px] text-gray-400 text-right">
            {websites.length} loaded{hasMore ? ' • scroll for more' : ''}
          </div>
        </div>
      )}
    </div>
  )
}

const Compliance = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'audit')

  const [websites, setWebsites] = useState([])
  const [selectedWebsiteId, setSelectedWebsiteId] = useState(searchParams.get('website') || null)
  const [websitesLoading, setWebsitesLoading] = useState(true)
  const [websitesPage, setWebsitesPage] = useState(1)
  const [websitesHasMore, setWebsitesHasMore] = useState(false)
  const [isLoadingMoreWebsites, setIsLoadingMoreWebsites] = useState(false)

  const [report, setReport] = useState(null)
  const [reportLoading, setReportLoading] = useState(Boolean(searchParams.get('website')))
  const [reportError, setReportError] = useState(null)

  const [remediations, setRemediations] = useState([])
  const [remediationsLoading, setRemediationsLoading] = useState(false)
  const [remediationsError, setRemediationsError] = useState(null)
  const [remediationsPage, setRemediationsPage] = useState(parseInt(searchParams.get('page')) || 1)
  const [remediationsTotalPages, setRemediationsTotalPages] = useState(1)
  const [remediationsTotalResults, setRemediationsTotalResults] = useState(0)

  const syncUrl = useCallback((updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      Object.entries(updates).forEach(([key, value]) => {
        if (value == null || value === '' || value === 'audit' || (key === 'page' && value === 1)) {
          next.delete(key)
        } else {
          next.set(key, String(value))
        }
      })
      return next
    })
  }, [setSearchParams])

  useEffect(() => {
    const fetchWebsites = async () => {
      try {
        setWebsitesLoading(true)
        const data = await websiteService.getAllWebsites({}, { page: 1, limit: WEBSITES_LIMIT })
        const list = data?.websites || data?.data || (Array.isArray(data) ? data : [])
        const totalPages = data?.totalPages || 1
        setWebsites(list)
        setWebsitesPage(1)
        setWebsitesHasMore(totalPages > 1)

        if (list.length > 0) {
          const urlWebsite = searchParams.get('website')
          const validId = urlWebsite && list.find((w) => getWebsiteId(w) === urlWebsite)
            ? urlWebsite
            : getWebsiteId(list[0])
          setSelectedWebsiteId(validId)
          if (!urlWebsite) syncUrl({ website: validId })
          // The selected site (from the URL) may live beyond page 1 — fetch it
          // directly so its name/URL render correctly in the dropdown.
          if (urlWebsite && !list.find((w) => getWebsiteId(w) === urlWebsite)) {
            try {
              const siteResponse = await websiteService.getWebsiteById(urlWebsite)
              const site = siteResponse?.website || siteResponse?.data || siteResponse
              if (site) setWebsites((prev) => [site, ...prev])
            } catch {
              // Falls back to showing "Select a website..." if the id is invalid
            }
          }
        }
      } catch {
        toast.error('Failed to load websites')
      } finally {
        setWebsitesLoading(false)
      }
    }
    fetchWebsites()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleLoadMoreWebsites = () => {
    if (isLoadingMoreWebsites || !websitesHasMore) return
    const nextPage = websitesPage + 1
    setIsLoadingMoreWebsites(true)
    websiteService
      .getAllWebsites({}, { page: nextPage, limit: WEBSITES_LIMIT })
      .then((data) => {
        const list = data?.websites || data?.data || (Array.isArray(data) ? data : [])
        const totalPages = data?.totalPages || 1
        setWebsites((prev) => [...prev, ...list])
        setWebsitesPage(nextPage)
        setWebsitesHasMore(nextPage < totalPages)
      })
      .catch(() => {
        toast.error('Failed to load more websites')
      })
      .finally(() => {
        setIsLoadingMoreWebsites(false)
      })
  }

  const fetchReport = useCallback(async (websiteId) => {
    if (!websiteId) return
    try {
      setReportLoading(true)
      setReportError(null)
      const data = await complianceService.getComplianceReport(websiteId)
      setReport(data)
    } catch (err) {
      setReportError(err)
    } finally {
      setReportLoading(false)
    }
  }, [])

  useEffect(() => {
    if (selectedWebsiteId) fetchReport(selectedWebsiteId)
  }, [selectedWebsiteId, fetchReport])

  const fetchRemediations = useCallback(async (websiteId, page = 1) => {
    if (!websiteId) return
    try {
      setRemediationsLoading(true)
      setRemediationsError(null)
      const data = await complianceService.getRemediations(websiteId, page, REMEDIATIONS_LIMIT)
      setRemediations(data?.data ?? [])
      setRemediationsPage(data?.page ?? page)
      setRemediationsTotalPages(data?.totalPages ?? 1)
      setRemediationsTotalResults(data?.totalResults ?? 0)
    } catch (err) {
      setRemediationsError(err)
    } finally {
      setRemediationsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (activeTab === 'remediation' && selectedWebsiteId) {
      fetchRemediations(selectedWebsiteId, remediationsPage)
    }
  // remediationsPage intentionally excluded — page changes go through handleRemediationsPageChange directly
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedWebsiteId, fetchRemediations])

  const handleRemediationsPageChange = (page) => {
    if (selectedWebsiteId && !remediationsLoading) {
      fetchRemediations(selectedWebsiteId, page)
      syncUrl({ page })
    }
  }

  const handleWebsiteChange = (websiteId) => {
    setSelectedWebsiteId(websiteId)
    setReport(null)
    setReportError(null)
    setReportLoading(true)
    setRemediations([])
    setRemediationsPage(1)
    setRemediationsTotalPages(1)
    setRemediationsTotalResults(0)
    syncUrl({ website: websiteId, page: 1 })
  }

  const handleTabChange = (tabId) => {
    setActiveTab(tabId)
    syncUrl({ tab: tabId, page: 1 })
  }

  const summary = report?.summary ?? {}
  const currentScore = summary.currentScore ?? 0
  const scoreChange = summary.scoreChange ?? 0
  const totalScans = summary.totalScans ?? 0
  const violationsFixed = summary.violationsFixed ?? 0
  const fixRate = summary.fixRate ?? 0
  const outstandingViolations = summary.outstandingViolations ?? 0

  return (
    <PageContainer
      title="Compliance Evidence"
      subtitle="Court-admissible audit trail, remediation records, and exportable compliance reports for your legal defense."
    >
      <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-indigo-600 to-primary text-white flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
            <Icon icon="mdi:gavel" className="text-2xl" />
          </div>
          <div>
            <p className="font-semibold">Final Phase — Legal Compliance Documentation</p>
            <p className="text-sm text-white/80 mt-0.5">
              Demonstrate good-faith accessibility efforts with immutable records, detailed remediation
              logs, and attorney-ready PDF reports.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="inline-flex items-center gap-1 text-xs font-medium bg-white/20 px-3 py-1.5 rounded-full">
            <Icon icon="mdi:shield-lock" />
            Immutable Records
          </span>
          <span className="inline-flex items-center gap-1 text-xs font-medium bg-white/20 px-3 py-1.5 rounded-full">
            <Icon icon="mdi:certificate" />
            Court-Admissible
          </span>
        </div>
      </div>

      {!websitesLoading && websites.length > 0 && (
        <div className="mb-4 flex items-center gap-3">
          <label className="text-sm font-medium text-text-dark whitespace-nowrap">Website:</label>
          <WebsiteSelect
            websites={websites}
            selectedWebsiteId={selectedWebsiteId}
            onChange={handleWebsiteChange}
            onLoadMore={handleLoadMoreWebsites}
            hasMore={websitesHasMore}
            isLoadingMore={isLoadingMoreWebsites}
          />
        </div>
      )}

      {reportLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-200 shrink-0" />
                <div className="flex-1">
                  <div className="h-3 bg-gray-200 rounded w-3/4 mb-2" />
                  <div className="h-6 bg-gray-200 rounded w-1/2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
          <StatCard
            icon="mdi:percent"
            iconColor="blue"
            value={`${currentScore}%`}
            label="Current Compliance Score"
            change={formatScoreChange(scoreChange)}
            changeType={scoreChange >= 0 ? 'positive' : 'negative'}
          />
          <StatCard
            icon="mdi:history"
            iconColor="cyan"
            value={totalScans.toLocaleString()}
            label="Timestamped Scans"
          />
          <StatCard
            icon="mdi:wrench-check"
            iconColor="green"
            value={violationsFixed.toLocaleString()}
            label="Violations Fixed"
            change={`${fixRate}% fix rate`}
            changeType="positive"
          />
          <StatCard
            icon="mdi:alert-circle-outline"
            iconColor="orange"
            value={outstandingViolations.toLocaleString()}
            label="Outstanding Violations"
          />
        </div>
      )}

      <div className="bg-white rounded-xl border border-border-light shadow-sm overflow-hidden">
        <div className="flex border-b border-border-light overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => handleTabChange(tab.id)}
              className={`flex items-center gap-2 px-4 md:px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
                activeTab === tab.id
                  ? 'border-primary text-primary bg-primary/5'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Icon icon={tab.icon} className="text-lg" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="p-4 md:p-6">
          {activeTab === 'audit' && <AuditTrailSection />}
          {activeTab === 'remediation' && (
            <RemediationLogSection
              remediations={remediations}
              isLoading={remediationsLoading}
              error={remediationsError}
              onRetry={() => fetchRemediations(selectedWebsiteId, 1)}
              currentPage={remediationsPage}
              totalPages={remediationsTotalPages}
              totalResults={remediationsTotalResults}
              onPageChange={handleRemediationsPageChange}
            />
          )}
          {activeTab === 'report' && (
            <ComplianceReportSection
              report={report}
              websiteId={selectedWebsiteId}
              isLoading={reportLoading}
              error={reportError}
              onRetry={() => fetchReport(selectedWebsiteId)}
            />
          )}
        </div>
      </div>
    </PageContainer>
  )
}

export default Compliance
